import * as React from 'react';
import { useMemo, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState, AppThunkDispatch } from "../../store";
import EntityList from "../../components/EntityList";
import { EntityType, Entity, UserEntity } from "../../store/entities/state";
import { loadEntities, entityCreating } from "../../store/entities/actions";
import { getUsersAsync } from "../../ajax/requests";
import { getEntitiesData } from "../../store/entities/selectors";
import { TK } from "../../store/translations/translationKeys";
import { canEditUsers } from '../../store/session/selectors';
import { getSettings } from '../../store/settings/selectors';
import { useTranslations } from '../../store/translations/hooks';
import { ComboField } from '../../components/Forms/fields';
import UserEditForm from './UserEditForm';
import UserItemContent from './UserItemContent';
import { translate } from '../../store/translations/hooks';

const NO_BRANCH = '__none__';

const Users: React.FC = () => {
    const t = useTranslations();
    const dispatch = useDispatch<AppThunkDispatch>();

    const entitiesData = useSelector((state: ApplicationState) => getEntitiesData<UserEntity>('users')(state));
    const canEdit = useSelector(canEditUsers);
    const branches = useSelector((state: ApplicationState) => getSettings(state).general?.branches ?? {});
    const hasBranches = Object.keys(branches).length > 0;

    const [branchFilter, setBranchFilter] = useState<string | undefined>(undefined);

    const branchName = useCallback((id: string) => branches[id]?.displayName || id, [branches]);

    const renderItemHeader = useCallback((entity: Entity) => {
        const user = entity as UserEntity;
        const names = hasBranches ? (user.branchIds ?? []).map(branchName) : [];
        return <UserItemContent user={user} branchNames={names} />;
    }, [hasBranches, branchName]);

    const itemFilter = useMemo(() => {
        if (!hasBranches || !branchFilter) return undefined;
        return (entity: Entity) => {
            const ids = (entity as UserEntity).branchIds ?? [];
            return branchFilter === NO_BRANCH ? ids.length === 0 : ids.includes(branchFilter);
        };
    }, [hasBranches, branchFilter]);

    const advancedOptions = useMemo(() => {
        if (!hasBranches) return undefined;
        return (
            <ComboField
                id="users-branch-filter"
                label={t(TK.userBranches)}
                value={branchFilter}
                options={[
                    { value: '', label: t(TK.all) },
                    ...Object.entries(branches).map(([key, b]) => ({ value: key, label: b.displayName || key })),
                    { value: NO_BRANCH, label: t(TK.noBranchGroup) },
                ]}
                onChange={value => setBranchFilter(value)}
            />
        );
    }, [hasBranches, branches, branchFilter, t]);

    const onLoad = useCallback((keyword?: string, offset?: number) =>
        dispatch(loadEntities<UserEntity>('users', (pageSize: number) => getUsersAsync(keyword, pageSize), offset ? 'AppendResult' : 'NewResult', () => !offset)),
        [dispatch]);

    const onCreateEntity = useCallback((entityType: EntityType) => dispatch(entityCreating(entityType)), [dispatch]);

    return (
        <EntityList
            id="users"
            entityType="users"
            title=""
            entitiesData={entitiesData}
            renderItemHeader={renderItemHeader}
            itemTabs={[
                { id: 'data', title: translate(TK.data), render: (_, entity, onClose) => (<UserEditForm data={entity as UserEntity} onClose={onClose} />) },
            ]}
            canCreateEntities={canEdit ? ['users'] : []}
            itemFilter={itemFilter}
            advancedOptions={advancedOptions}
            onLoad={onLoad}
            onCreateEntity={onCreateEntity}
        />
    );
};

export default Users;
